import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import PhotoUpload from '../checkout/PhotoUpload';
import ShareButton from '../checkout/ShareButton';
import FAQ from '../checkout/FAQ';
import CouponBox from '../checkout/CouponBox';

import MusicPlayer from './components/MusicPlayer';
import VersionToggle from './components/VersionToggle';
import PriceMicroCard from './components/PriceMicroCard';
import LyricsExpandable from './components/LyricsExpandable';
import PackageDuploCard from './components/PackageDuploCard';
import GuaranteeCard from './components/GuaranteeCard';
import StickyCTABar from './components/StickyCTABar';
import TestimonialsCarousel from './components/TestimonialsCarousel';
import PaymentBottomSheet from './components/PaymentBottomSheet';

import type {
  CheckoutInfo,
  CupomCache,
  CupomPriced,
  Variante,
} from './types';

const cupomStorageKey = (slug: string) => `inspira-cupom-${slug}`;

async function validateCupomFor(
  slug: string,
  code: string,
  sel: 'A' | 'AB',
): Promise<CupomPriced | null> {
  try {
    const r = await fetch('/api/checkout?action=validar-cupom', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug, cupom: code, previas_selecionadas: sel }),
    });
    const d = await r.json().catch(() => ({}));
    if (!d?.valid) return null;
    return {
      valor_base: Number(d.valor_base),
      valor_final: Number(d.valor_final),
      desconto: Number(d.desconto),
    };
  } catch {
    return null;
  }
}

export default function MusicaPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();

  const [info, setInfo] = useState<CheckoutInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Variante ativa no player (independente da seleção de compra)
  const [variante, setVariante] = useState<Variante>('A');
  // Cliente comprando ambas? (combina com a variante atual pra montar selecao)
  const [ambas, setAmbas] = useState(false);
  // Bottom-sheet de pagamento
  const [paymentOpen, setPaymentOpen] = useState(false);
  // Estado de cupom
  const [cupomCache, setCupomCache] = useState<CupomCache>(null);
  const [cupomInvalidCode, setCupomInvalidCode] = useState<string | null>(null);
  const [showCouponInput, setShowCouponInput] = useState(false);

  const cupomHydratedRef = useRef(false);
  const selecaoHydratedRef = useRef(false);

  // ─── URL params ────────────────────────────────────────────────────────────
  const urlCupom = useMemo(() => {
    const raw = searchParams.get('cupom');
    if (!raw) return null;
    return String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 24) || null;
  }, [searchParams]);

  const urlVariante = useMemo(() => {
    const raw = searchParams.get('v');
    if (!raw) return null;
    return String(raw).toUpperCase().replace(/[^AB]/g, '').split('').sort().join('').slice(0, 2) || null;
  }, [searchParams]);

  // ─── Load pedido ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) {
      setError('Link inválido. Fale com a Inspira no WhatsApp.');
      setLoading(false);
      return;
    }
    fetch(`/api/checkout?action=info&slug=${encodeURIComponent(slug)}`)
      .then(async (r) => {
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.error || 'Pedido não encontrado');
        }
        return r.json();
      })
      .then((data: CheckoutInfo) => setInfo(data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [slug]);

  // ─── Selecao derivada ──────────────────────────────────────────────────────
  const selectionString = useMemo(() => (ambas ? 'AB' : variante), [ambas, variante]);
  const selectionLabel = useMemo(() => {
    if (ambas) return 'Versões I + II — presente em dobro';
    return `Versão ${variante === 'A' ? 'I' : 'II'}`;
  }, [ambas, variante]);

  // ─── Cupom ─────────────────────────────────────────────────────────────────
  const cupom = useMemo(() => {
    if (!cupomCache) return null;
    const target = selectionString === 'AB' ? cupomCache.ab : cupomCache.one;
    if (!target) return null;
    return { codigo: cupomCache.codigo, desconto: target.desconto, valor_final: target.valor_final };
  }, [cupomCache, selectionString]);

  const valores = info?.valores ?? { uma: 49.9, ambas: 69.9 };
  const valorBase = ambas ? valores.ambas : valores.uma;
  const valorAtual = cupom ? cupom.valor_final : valorBase;
  const descontoPct = useMemo(() => {
    if (!cupom || valorBase <= 0) return null;
    const pct = Math.round(((valorBase - cupom.valor_final) / valorBase) * 100);
    return pct > 0 ? pct : null;
  }, [cupom, valorBase]);

  const applyCupomCode = useCallback(
    async (rawCode: string, opts?: { silent?: boolean }) => {
      if (!slug) return false;
      const code = rawCode.trim().toUpperCase().slice(0, 24);
      if (!code) return false;
      const [resA, resAB] = await Promise.all([
        validateCupomFor(slug, code, 'A'),
        validateCupomFor(slug, code, 'AB'),
      ]);
      if (!resA && !resAB) {
        try { sessionStorage.removeItem(cupomStorageKey(slug)); } catch { /* noop */ }
        if (!opts?.silent) setCupomInvalidCode(code);
        return false;
      }
      setCupomCache({ codigo: code, one: resA, ab: resAB });
      setCupomInvalidCode(null);
      try { sessionStorage.setItem(cupomStorageKey(slug), code); } catch { /* noop */ }
      try {
        (window as any).gtag?.('event', 'cupom_aplicado', {
          slug,
          cupom: code,
          valor_uma: resA?.valor_final ?? null,
          valor_ambas: resAB?.valor_final ?? null,
        });
      } catch { /* noop */ }
      return true;
    },
    [slug],
  );

  const removeCupom = useCallback(() => {
    setCupomCache(null);
    setCupomInvalidCode(null);
    if (slug) {
      try { sessionStorage.removeItem(cupomStorageKey(slug)); } catch { /* noop */ }
    }
  }, [slug]);

  // Hidratar variante: URL → DB (info.previas_selecionadas)
  useEffect(() => {
    if (selecaoHydratedRef.current) return;
    if (!info) return;
    const previaPronta = !!info.previas.a?.audio_url && !!info.previas.b?.audio_url;
    if (!previaPronta) return;
    const source = urlVariante || info.previas_selecionadas;
    if (!source) {
      selecaoHydratedRef.current = true;
      return;
    }
    selecaoHydratedRef.current = true;
    if (source === 'AB') {
      setAmbas(true);
      setVariante('A');
    } else if (source === 'B') {
      setVariante('B');
      setAmbas(false);
    } else {
      setVariante('A');
      setAmbas(false);
    }
  }, [info, urlVariante]);

  // Hidratar cupom: URL → sessionStorage
  useEffect(() => {
    if (cupomHydratedRef.current) return;
    if (!info || !slug) return;
    const previaPronta = !!info.previas.a?.audio_url && !!info.previas.b?.audio_url;
    if (!previaPronta) return;
    cupomHydratedRef.current = true;

    const fromStorage = (() => {
      try { return sessionStorage.getItem(cupomStorageKey(slug)); }
      catch { return null; }
    })();
    const code = urlCupom || fromStorage;
    if (!code) return;
    applyCupomCode(code, { silent: !urlCupom }).then((ok) => {
      if (!ok && urlCupom) {
        try { (window as any).gtag?.('event', 'cupom_url_invalido', { slug, cupom_tentado: urlCupom }); }
        catch { /* noop */ }
      }
    });
  }, [info, slug, urlCupom, applyCupomCode]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleVarianteChange = useCallback((v: Variante) => {
    setVariante(v);
    setAmbas(false);
  }, []);

  const handleTogglePackage = useCallback(() => {
    setAmbas((prev) => !prev);
  }, []);

  const handleOpenPayment = useCallback(() => {
    setPaymentOpen(true);
  }, []);

  const handleCouponBoxApply = useCallback(
    (cup: { codigo: string } | null) => {
      if (!cup) {
        removeCupom();
        setShowCouponInput(false);
        return;
      }
      applyCupomCode(cup.codigo);
    },
    [applyCupomCode, removeCupom],
  );

  const handlePaymentApproved = useCallback(() => {
    if (slug) window.location.href = `/ouvir-musica/${slug}/sucesso`;
  }, [slug]);

  // ─── Estados especiais ─────────────────────────────────────────────────────
  if (loading) {
    return <Shell><Centered>Abrindo o pedido…</Centered></Shell>;
  }

  if (error || !info) {
    return (
      <Shell>
        <Centered>
          <h1 className="mz-err-title">Não conseguimos abrir esse link</h1>
          <p className="mz-err-body">{error || 'Pedido não encontrado.'}</p>
          <a href="https://wa.me/5511926797492" className="mz-err-cta">Falar com a Inspira</a>
        </Centered>
      </Shell>
    );
  }

  if (
    info.status === 'pago' ||
    info.status === 'gerando_musica' ||
    info.status === 'entregue'
  ) {
    return (
      <Shell>
        <Centered>
          <div className="mz-success-icon" aria-hidden>
            <svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </div>
          <h1 className="mz-success-title">Pagamento confirmado</h1>
          <p className="mz-success-body">
            A música pra <em>{info.nome_homenageado}</em> já entrou em produção. Em até 30 minutos a gente envia no WhatsApp. 💛
          </p>
          <a href="https://wa.me/5511926797492" className="mz-err-cta">Voltar pro WhatsApp</a>
        </Centered>
      </Shell>
    );
  }

  const previaA = info.previas.a;
  const previaB = info.previas.b;
  const previaPronta = !!previaA?.audio_url && !!previaB?.audio_url;
  const audioAtivo = variante === 'A' ? previaA?.audio_url ?? null : previaB?.audio_url ?? null;
  const letraAtiva = variante === 'A' ? previaA?.letra ?? null : previaB?.letra ?? null;

  return (
    <Shell>
      <SEOMeta
        title="Sua música — Inspira Músicas"
        description="Ouça as duas versões e escolha a que mais te emocionou"
        path={`/ouvir-musica/${slug}`}
        noIndex
      />

      <main className="mz-page">
        {/* Header enxuto: só logo e badge sutil de "prévia" */}
        <header className="mz-head">
          <img src="/logo-light.png" alt="Inspira Músicas" className="mz-logo mz-logo-light" />
          <img src="/logo-dark.png" alt="Inspira Músicas" className="mz-logo mz-logo-dark" />
          <div className="mz-head-eyebrow">
            <span className="mz-head-dot" />
            Prévia · {info.nome_homenageado || 'sua pessoa'}
          </div>
        </header>

        {/* Player heroico — vinil giratório */}
        {previaPronta ? (
          <MusicPlayer
            variante={variante}
            audioUrl={audioAtivo}
            fotoUrl={info.foto_url}
            nomeHomenageado={info.nome_homenageado}
          />
        ) : (
          <PlayerSkeleton />
        )}

        {/* Toggle Versão I / Versão II */}
        {previaPronta && (
          <VersionToggle
            variante={variante}
            onChange={handleVarianteChange}
            disabled={ambas}
          />
        )}

        {/* Micro-card de preço — sempre visível, ancora valor + nome */}
        {previaPronta && (
          <PriceMicroCard
            nomeHomenageado={info.nome_homenageado}
            valorFinal={valorAtual}
            valorBase={valorBase}
            cupomCodigo={cupom?.codigo ?? null}
            descontoPct={descontoPct}
          />
        )}

        {/* Letra expansível — renderiza só quando a API trouxer */}
        {previaPronta && letraAtiva && (
          <LyricsExpandable
            letra={letraAtiva}
            nomeHomenageado={info.nome_homenageado}
          />
        )}

        {/* Upsell "leve as duas" */}
        {previaPronta && (
          <PackageDuploCard
            selected={ambas}
            onToggle={handleTogglePackage}
            valorUma={valores.uma}
            valorAmbas={valores.ambas}
            cupomCodigo={cupom?.codigo ?? null}
            valorAmbasFinal={cupomCache?.ab?.valor_final ?? null}
          />
        )}

        {/* Foto de capa (opcional) */}
        {previaPronta && (
          <div className="mz-photo-wrap">
            <PhotoUpload
              slug={slug || ''}
              initialFotoUrl={info.foto_url}
              nomeHomenageada={info.nome_homenageado}
            />
          </div>
        )}

        {/* Garantia em destaque */}
        <GuaranteeCard />

        {/* Depoimentos sociais */}
        <TestimonialsCarousel />

        {/* Cupom — saída de emergência */}
        {previaPronta && !cupom && (
          <div className="mz-coupon-row">
            {!showCouponInput ? (
              <button
                type="button"
                onClick={() => setShowCouponInput(true)}
                className="mz-coupon-link"
              >
                Tenho um código de desconto
              </button>
            ) : (
              <div className="mz-coupon-box">
                <CouponBox
                  slug={slug || ''}
                  selectionString={selectionString || 'A'}
                  applied={null}
                  onApply={handleCouponBoxApply}
                  initialCode={cupomInvalidCode || undefined}
                />
              </div>
            )}
          </div>
        )}
        {previaPronta && cupom && (
          <div className="mz-coupon-row">
            <button
              type="button"
              onClick={() => handleCouponBoxApply(null)}
              className="mz-coupon-link"
            >
              Remover cupom {cupom.codigo}
            </button>
          </div>
        )}

        {/* Ações secundárias: compartilhar + falar */}
        {previaPronta && (
          <div className="mz-actions">
            <ShareButton slug={slug || ''} cupomCodigo={cupom?.codigo ?? null} />
            <a href="https://wa.me/5511926797492" className="mz-secondary-cta">
              💬 Falar com a Inspira
            </a>
          </div>
        )}

        {/* FAQ */}
        <FAQ />

        {/* Footer enxuto */}
        <footer className="mz-foot">
          <div className="mz-foot-line">AUTOMAT TECNOLOGIA DA INFORMAÇÃO LTDA · CNPJ 59.640.197/0001-76</div>
          <div className="mz-foot-line">Rua Pais Leme, 215, Pinheiros · São Paulo/SP · contato@inspiramusicas.com.br</div>
        </footer>

        {/* Espaço pro sticky bar não cobrir conteúdo */}
        <div style={{ height: 92 }} aria-hidden />
      </main>

      {/* Sticky CTA — sempre visível depois das prévias prontas */}
      {previaPronta && (
        <StickyCTABar
          valor={valorAtual}
          visible={!paymentOpen}
          onClick={handleOpenPayment}
          selectionLabel={selectionLabel}
        />
      )}

      {/* Bottom-sheet de pagamento */}
      {previaPronta && (
        <PaymentBottomSheet
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          slug={slug || ''}
          selectionString={selectionString}
          valor={valorAtual}
          cupomCodigo={cupom?.codigo ?? null}
          nomeHomenageado={info.nome_homenageado}
          onApproved={handlePaymentApproved}
        />
      )}

      <style>{`
        .mz-page {
          max-width: 720px;
          margin: 0 auto;
          padding: 28px 0 22px;
          position: relative;
          z-index: 1;
        }

        .mz-head {
          text-align: center;
          padding: 0 16px 18px;
        }
        .mz-logo {
          height: 30px;
          width: auto;
          margin-bottom: 14px;
          display: inline-block;
        }
        .mz-logo-dark { display: none; }
        html[data-theme='dark'] .mz-logo-light { display: none; }
        html[data-theme='dark'] .mz-logo-dark { display: inline-block; }

        .mz-head-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--amber-deep);
          padding: 6px 14px;
          background: var(--gold-pale);
          border: 1px solid var(--gold-border);
          border-radius: 999px;
        }
        .mz-head-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--gold);
          box-shadow: 0 0 0 3px rgba(255,145,77,0.20);
          animation: mz-pulse-dot 1.8s ease-in-out infinite;
        }
        @keyframes mz-pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.9); }
        }

        .mz-photo-wrap {
          margin: 0 16px 22px;
        }
        @media (min-width: 720px) {
          .mz-photo-wrap { margin: 0 auto 26px; max-width: 660px; }
        }

        .mz-coupon-row {
          text-align: center;
          margin: 4px 16px 16px;
        }
        .mz-coupon-link {
          background: transparent;
          border: none;
          color: var(--text-soft);
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: underline;
          text-underline-offset: 3px;
          padding: 6px;
        }
        .mz-coupon-link:hover { color: var(--amber-deep); }
        .mz-coupon-box { margin-top: 6px; text-align: left; }

        .mz-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin: 0 16px 22px;
        }
        @media (min-width: 720px) {
          .mz-actions { margin: 0 auto 26px; max-width: 660px; }
        }
        .mz-secondary-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 14px 12px;
          border-radius: 999px;
          background: transparent;
          color: var(--text-strong);
          border: 1.5px solid var(--border-soft);
          font-family: 'DM Sans', sans-serif;
          font-weight: 700;
          font-size: 13.5px;
          text-decoration: none;
          transition: all 0.18s;
        }
        .mz-secondary-cta:hover {
          border-color: var(--gold);
          color: var(--gold);
        }

        .mz-foot {
          text-align: center;
          padding: 18px 16px 4px;
          color: var(--text-soft);
          font-family: 'DM Sans', sans-serif;
          font-size: 10.5px;
          line-height: 1.55;
        }
        .mz-foot-line { margin-bottom: 4px; }

        /* Estados especiais */
        .mz-err-title {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-style: italic;
          font-weight: 700;
          font-size: 26px;
          color: var(--heading-ink);
          letter-spacing: -0.02em;
          margin: 0 0 12px;
        }
        .mz-err-body {
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: var(--text-muted);
          line-height: 1.5;
          max-width: 360px;
          margin: 0 0 22px;
        }
        .mz-err-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 26px;
          background: linear-gradient(135deg, var(--gold) 0%, var(--gold-hover) 100%);
          color: #fff;
          border-radius: 999px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 700;
          font-size: 14.5px;
          text-decoration: none;
          box-shadow: 0 6px 18px rgba(255,145,77,0.42);
        }

        .mz-success-icon {
          color: #1f8a5b;
          margin-bottom: 18px;
        }
        .mz-success-title {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-style: italic;
          font-weight: 700;
          font-size: 30px;
          color: var(--heading-ink);
          letter-spacing: -0.02em;
          margin: 0 0 14px;
        }
        .mz-success-body {
          font-family: 'DM Sans', sans-serif;
          font-size: 15.5px;
          color: var(--text-muted);
          line-height: 1.5;
          max-width: 420px;
          margin: 0 0 22px;
        }
        .mz-success-body em {
          font-style: italic;
          color: var(--gold);
          font-family: 'Playfair Display', serif;
          font-weight: 700;
        }
      `}</style>
    </Shell>
  );
}

// ─── Pequenos componentes locais ─────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--cream)', position: 'relative' }}>
      {children}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 24px',
      }}
    >
      {children}
    </div>
  );
}

function PlayerSkeleton() {
  return (
    <div className="mz-skel">
      <div className="mz-skel-disc" />
      <div className="mz-skel-text" />
      <style>{`
        .mz-skel {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 30px 16px;
        }
        .mz-skel-disc {
          width: min(72vw, 280px);
          aspect-ratio: 1;
          border-radius: 50%;
          background:
            radial-gradient(circle, var(--border-soft) 0%, var(--warm-white) 100%);
          animation: mz-skel-pulse 1.6s ease-in-out infinite;
          margin-bottom: 24px;
        }
        .mz-skel-text {
          width: 60%;
          max-width: 280px;
          height: 32px;
          border-radius: 8px;
          background: var(--border-soft);
          animation: mz-skel-pulse 1.6s ease-in-out infinite;
        }
        @keyframes mz-skel-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}
